import React, { createContext, useContext, useState, ReactNode } from "react";

// Define the available themes
export type Theme = "theme-light" | "theme-dark" | "theme-blue" | "theme-green";

// Define the shape of the ThemeContext
interface ThemeContextType {
  theme: Theme;
  toggleTheme: (newTheme: Theme) => void;
}

// Create the context with an initial value
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Define the props for the ThemeProvider
interface ThemeProviderProps {
  children: ReactNode;
}

// Create the ThemeProvider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>("theme-light");

  const toggleTheme = (newTheme: Theme) => {
    setTheme(newTheme);
    document.documentElement.className = newTheme;
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the ThemeContext
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
